/**
 * Entry point: boot the app into the main menu, then transition into an
 * in-game session when the player clicks New Game. v2M1 introduced this
 * shell; prior to v2M1 the entry point booted directly into gameplay.
 *
 * Lifecycle model (UI_UX §9.3):
 *   App start ──▶ Main menu
 *                   ├──▶ New Game ──▶ [setup ──▶] InGame
 *                   ├──▶ Load Game ── stubbed (v2M2)
 *                   ├──▶ Settings  ── stubbed (v2M4)
 *                   └──▶ Exit      ── confirm
 *
 *   InGame ─── (Return to Main Menu) ──▶ Main menu
 *
 * Canvas + LoadedGameData live for the app's full lifetime; the session
 * (sim loop, render loop, UI mounts) is torn down and recreated on each
 * Main Menu round-trip.
 */

import { loadGameData, type LoadedGameData } from './data/loader';
import { createInitialGameState, type NewGameOptions } from './game/scenario';
import { loadKeyArtImage, loadStarfieldImage } from './render/backdrop';
import { loadBiomeTextureImage } from './render/biome-textures';
import { loadColoredShipSpriteImage, loadShipSpriteImage } from './render/ship-sprites';
import { EMPIRE_COLOR_PALETTE } from './state/empire-colors';
import { loadSpaceStructureSpriteImage } from './render/space-structure-sprites';
import { loadTerrainSpriteImage } from './render/terrain-sprites';
import { loadPlanetStructureSpriteImage } from './render/planet-structure-sprites';
import { serializeGameState } from './save/serialize';
import { createSaveStorage, type SaveStorage } from './save/storage';
import {
  createSettingsStorage,
  type SettingsStorage,
} from './settings/storage';
import type { Settings } from './settings/types';
import type { BiomeKind, EnvironmentKind, GameState, StructureKind } from './state/types/index';
import { ODP_PLATFORM_CHASSIS, SPACE_STRUCTURE_KINDS } from './game/balance-space-structures';
import { mountMainMenu, type MainMenuHandle } from './ui/main-menu';
import {
  mountNewGameSetup,
  type NewGameSetupHandle,
} from './ui/new-game-setup';
import {
  mountLoadSlotPicker,
  type LoadSlotPickerHandle,
} from './ui/load-slot-picker';
import {
  mountSaveSlotPicker,
  type SaveSlotPickerHandle,
} from './ui/save-slot-picker';
import {
  mountSettingsOverlay,
  type SettingsOverlayHandle,
} from './ui/settings-overlay';
import { startGameSession, type SessionHandle } from './ui/session';
import {
  getAudioEngine,
  loadMissileSamples,
  registerAlertCues,
  registerColonyCues,
  registerMusicCues,
  registerShipCues,
  registerStructureCues,
  registerUiCues,
  registerWeaponCues,
} from './audio';

type AppMode =
  | { kind: 'main-menu'; handle: MainMenuHandle }
  | { kind: 'new-game-setup'; handle: NewGameSetupHandle }
  | { kind: 'in-game'; session: SessionHandle };

interface AppContext {
  canvas: HTMLCanvasElement;
  data: LoadedGameData;
  storage: SaveStorage;
  settingsStorage: SettingsStorage;
  /**
   * Live Settings — replaced wholesale by `enterSettings`'s onSave path
   * so any caller using `getSettings()` (e.g. the speed controller seed
   * or the in-game-menu confirm gates) reads fresh values immediately.
   */
  settings: Settings;
  /** Save-picker handle — non-null only while a Save picker is mounted on top of the in-game menu. */
  savePicker: SaveSlotPickerHandle | null;
  /** Load-picker handle — non-null only while a Load picker is mounted on top of the main menu. */
  loadPicker: LoadSlotPickerHandle | null;
  /** Settings-overlay handle — non-null only while the Settings overlay is mounted (over either menu). */
  settingsOverlay: SettingsOverlayHandle | null;
  mode: AppMode | null;
}

function bootstrap(): void {
  try {
    const canvas = document.getElementById('game-canvas') as HTMLCanvasElement | null;
    if (!canvas) throw new Error('Expected <canvas id="game-canvas"> in DOM');
    const data = loadGameData();
    const storage = createSaveStorage();
    const settingsStorage = createSettingsStorage();
    const settings = settingsStorage.load();

    const ctx: AppContext = {
      canvas,
      data,
      storage,
      settingsStorage,
      settings,
      savePicker: null,
      loadPicker: null,
      settingsOverlay: null,
      mode: null,
    };

    // On window unload, tear down whichever mode is active so the render/sim
    // loops stop cleanly. Browsers will reap anyway, but a clean stop helps
    // when hot-reloading in dev.
    window.addEventListener('beforeunload', () => {
      teardownCurrent(ctx);
    });

    // v2M8.1 autoplay gating — browsers start AudioContext in 'suspended'
    // state until a user gesture. Boot the engine here (allocates the
    // context but doesn't make sound yet); resume on first click / keydown
    // so the main-menu buttons or any hotkey unlocks audio. Listener
    // self-removes after the first call.
    const audio = getAudioEngine();
    registerUiCues(audio);
    registerAlertCues(audio);
    registerWeaponCues(audio);
    registerShipCues(audio);
    registerStructureCues(audio);
    registerMusicCues(audio);
    registerColonyCues(audio);
    // v3M28.6 — race-replace the four procedural missile cues with
    // the commissioned ElevenLabs samples. Fire-and-forget: procedural
    // fallbacks stay active until each decode resolves (typically a
    // few hundred ms post-boot, well before the first missile fires).
    void loadMissileSamples(audio);
    // v2M8.8 — seed bus volumes from persisted settings. Engine
    // already has sensible defaults pre-load, so a fresh-install
    // first-run isn't deafening; this just overrides with whatever
    // the player saved.
    audio.applyVolumes(settings.audio);
    const unlockAudio = (): void => {
      audio.tryResume();
      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
    };
    window.addEventListener('pointerdown', unlockAudio);
    window.addEventListener('keydown', unlockAudio);

    // v2M8.2 UI SFX wiring — global delegated listeners for `<button>`
    // click + hover. Skips disabled buttons and any element tagged with
    // `data-no-sound`. Per-overlay panel-open / panel-close cues fire
    // from the individual mount* functions (in-game-menu.ts,
    // settings-overlay.ts, save-slot-picker.ts, load-slot-picker.ts,
    // research-panel.ts). HUD / detail-panel / mini-galaxy stay quiet —
    // they're persistent UI, not button-driven panels.
    document.addEventListener('pointerdown', (e) => {
      const btn = (e.target as HTMLElement | null)?.closest?.('button');
      if (!btn || btn.disabled) return;
      if ((btn as HTMLElement).dataset.noSound !== undefined) return;
      audio.playCue('ui:click');
    });
    // MFix5.1 — dedupe hover SFX so a stationary cursor over one button
    // doesn't replay the cue every render tick. Two contributing causes:
    //   1. `pointerover` bubbles, firing on each child-boundary crossing
    //      within a button (text nodes, label spans).
    //   2. Detail-panel rebuilds its button DOM every tick (~100 ms),
    //      generating fake pointerout/over cycles under a stationary
    //      cursor as the underlying element gets replaced.
    // Dedupe by `textContent` (stable across rebuilds for the same
    // logical button) and clear only when the cursor moves to a
    // non-button area (`pointermove` outside any <button>).
    let lastHoverKey: string | null = null;
    document.addEventListener('pointerover', (e) => {
      const btn = (e.target as HTMLElement | null)?.closest?.('button');
      if (!btn || btn.disabled) return;
      if ((btn as HTMLElement).dataset.noSound !== undefined) return;
      const key = btn.textContent ?? '';
      if (key === lastHoverKey) return;
      lastHoverKey = key;
      audio.playCue('ui:hover');
    });
    document.addEventListener('pointermove', (e) => {
      const btn = (e.target as HTMLElement | null)?.closest?.('button');
      if (!btn) lastHoverKey = null;
    });

    // v2M8.8 — M-hotkey master mute (AUDIO §11). Skips if a text
    // input has focus so it doesn't swallow letter keys mid-typing.
    window.addEventListener('keydown', (e) => {
      if (e.key !== 'm' && e.key !== 'M') return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;
      audio.toggleMute();
    });

    // v2M8.8 — focus-loss fade (AUDIO §11). Master gain ramps to 0
    // over 200 ms when the tab loses focus and back to slider value
    // on focus return. Slider value itself is unaffected.
    window.addEventListener('blur', () => {
      audio.setFocusLost(true);
    });
    window.addEventListener('focus', () => {
      audio.setFocusLost(false);
    });

    // v4M1.8a — opportunistic backdrop sample swap. Fires once at app
    // boot, attempts each commissioned-art slot in parallel, swallows
    // 404s. Procedural fallbacks stay active until a sample lands —
    // and individual successes apply independently, so a half-shipped
    // asset pass (e.g. only the key art is ready) still upgrades the
    // surfaces that have art available. Not awaited; the menu mounts
    // immediately and any successful swap replaces the cache entry on
    // the next `getX` call.
    void tryLoadCommissionedBackdrops();

    // v4M4.a — same opportunistic-load pattern, applied to per-(race,
    // chassis) ship sprites. Iterates the loaded shipTemplates map and
    // probes a stable URL for each; 404s leave the procedural per-race
    // silhouette active in `ship-draw.ts`. As with backdrops, successes
    // apply independently — half-shipped sprite passes still upgrade
    // every chassis that has art on disk.
    void tryLoadCommissionedShipSprites(data);

    // v4M4.b — same pattern applied to space structures (Constructor-
    // built chassis like Warp Gate / ODP / Refinery — race-neutral) and
    // planet structures (Shipyard / Research Lab / etc. — also race-
    // neutral). 404s leave the procedural silhouettes active in
    // `ship-draw.ts:drawSpaceStructure` + `system-view.ts:drawStructureGlyph`.
    void tryLoadCommissionedSpaceStructureSprites();
    void tryLoadCommissionedPlanetStructureSprites(data);

    // v4M13.1 — opportunistic load of commissioned terrain sprites
    // (asteroid-field / exotic-gas-cloud / nebula / black-hole /
    // ion-cloud / radiation-belt). Race-neutral; 404s leave the
    // procedural per-kind silhouettes active in
    // `system-view.ts:drawEnvironmentEntities`. Sprite assets land
    // at v4M13.2; until then the load attempts all 404 and the
    // visual read is identical to pre-v4M13.
    void tryLoadCommissionedTerrainSprites();

    enterMainMenu(ctx);
  } catch (err) {
    showBootstrapError(err);
    throw err;
  }
}

/**
 * v4M1.8a — opportunistic load of all commissioned backdrops. Each
 * file lives at a stable `/assets/backdrops/...` path. Vite serves
 * `public/` as the web root, so dropping a file into
 * `public/assets/backdrops/key-art.png` makes it available at
 * `/assets/backdrops/key-art.png` without further build wiring.
 *
 * All 10 loads fire in parallel; the returned promise resolves once
 * the slowest settles (success or failure). Caller does not need to
 * await — successes mutate caches in `backdrop.ts` / `biome-textures.ts`
 * directly, and subsequent `getX` calls pick up the new entries.
 *
 * Expected files (procedural fallbacks active in absence of any):
 *   /assets/backdrops/key-art.png           — main menu (1920×1080)
 *   /assets/backdrops/galaxy-starfield.png  — galaxy view (1024×1024 tileable)
 *   /assets/backdrops/system-starfield.png  — system view (1024×1024 tileable)
 *   /assets/backdrops/biome-<kind>.png      — per-biome (128×128) — 7 files,
 *                                              one per BiomeKind enum value.
 */
async function tryLoadCommissionedBackdrops(): Promise<void> {
  const biomes: BiomeKind[] = [
    'gaia',
    'temperate',
    'desert',
    'arctic',
    'ocean',
    'volcanic',
    'barren',
  ];
  const tasks: Promise<void>[] = [
    loadKeyArtImage('/assets/backdrops/key-art.png'),
    loadStarfieldImage('galaxy', '/assets/backdrops/galaxy-starfield.png'),
    loadStarfieldImage('system', '/assets/backdrops/system-starfield.png'),
    ...biomes.map((b) =>
      loadBiomeTextureImage(b, `/assets/backdrops/biome-${b}.png`),
    ),
  ];
  await Promise.all(tasks);
}

/**
 * v4M4.a — opportunistic load of all commissioned ship sprites.
 *
 * Iterates the loaded `shipTemplates` map (cache key already in
 * `${raceId}:${chassis}` form) and probes a stable URL per chassis at
 * `/assets/ships/<race-slug>-<chassis-slug>.png`. Promise contract
 * mirrors the backdrop pass: each per-chassis load never rejects, so a
 * 404 on one slot leaves the procedural per-race silhouette active for
 * that chassis without taking down the rest of the batch.
 *
 * Slug rule: lowercase, non-alphanumerics collapsed to single hyphens
 * (e.g. RACE-AA / "Heavy Cruiser" → `race-aa-heavy-cruiser.png`,
 * "Energy Weapons Platform" → `race-aa-energy-weapons-platform.png`).
 * Documented for the commissioned-art pipeline in
 * GRAPHICS.md §4.2 alongside the 128×128 nose-up sprite contract.
 */
async function tryLoadCommissionedShipSprites(
  data: LoadedGameData,
): Promise<void> {
  const tasks: Promise<void>[] = [];
  for (const tmpl of data.shipTemplates.values()) {
    const raceSlug = tmpl.raceId.toLowerCase();
    const chassisSlug = tmpl.chassis
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    // v4M4.a — neutral single-sprite probe per (race, chassis). Used
    // by the MFix13 Option B multiply-tint render path and by races
    // that don't have per-color variants yet.
    const neutralUrl = `/assets/ships/${raceSlug}-${chassisSlug}.png`;
    tasks.push(loadShipSpriteImage(tmpl.raceId, tmpl.chassis, neutralUrl));
    // MFix14 — per-color variant probes. Loader is non-rejecting per
    // the v4M4.a contract, so 404s on colors not authored yet (e.g.
    // RACE-AA green / cyan / blue) silently leave the colored cache
    // empty and the renderer falls through to the neutral + multiply-
    // tint path. Probing all races for all colors costs N_races ×
    // N_chassis × N_colors fetches at boot (currently 3 × 13 × 8 =
    // 312, almost all expected 404 for races without variants yet);
    // each 404 is small and parallelised, so the boot cost is the
    // network round-trip floor not a per-request cost. Acceptable
    // for the v4M4 rollout window; revisit if it becomes painful.
    for (const colorEntry of EMPIRE_COLOR_PALETTE) {
      const colorUrl = `/assets/ships/${raceSlug}-${chassisSlug}-${colorEntry.id}.png`;
      tasks.push(
        loadColoredShipSpriteImage(
          tmpl.raceId,
          tmpl.chassis,
          colorEntry.id,
          colorUrl,
        ),
      );
    }
  }
  await Promise.all(tasks);
}

/**
 * v4M4.b — opportunistic load of all commissioned space-structure
 * sprites. Iterates the static `SPACE_STRUCTURE_KINDS` list and probes
 * `/assets/structures/space-<chassis-slug>.png` per chassis. Race-
 * neutral (the procedural silhouettes in
 * `ship-draw.ts:drawSpaceStructure` are race-neutral by design, so a
 * single sprite serves all races). Slug rule matches v4M4.a:
 * lowercase, non-alphanumerics collapsed to single hyphens (e.g.
 * "Warp Gate" → `space-warp-gate.png`, "Energy Weapons Platform" →
 * `space-energy-weapons-platform.png`). Documented in GRAPHICS.md §4.2.
 */
async function tryLoadCommissionedSpaceStructureSprites(): Promise<void> {
  const tasks: Promise<void>[] = [];
  for (const chassis of SPACE_STRUCTURE_KINDS) {
    const chassisSlug = chassis
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    const url = `/assets/structures/space-${chassisSlug}.png`;
    tasks.push(loadSpaceStructureSpriteImage(chassis, url));
  }
  // MFix42.6 (2026-06-01) — `ODP_PLATFORM_CHASSIS` is the synthetic ring-
  // node chassis spawned by `advance-odp.ts` when an ODP command structure
  // activates. It's deliberately ABSENT from `SPACE_STRUCTURE_KINDS` (the
  // Constructor-buildable roster) per the `balance-space-structures.ts`
  // comment "the player never builds them directly". The sprite still
  // needs to load though — `drawSpaceStructure` consults
  // `getSpaceStructureSprite(chassisId)` for every space-structure ship
  // regardless of build provenance. Probe it explicitly here.
  const odpSlug = ODP_PLATFORM_CHASSIS
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  tasks.push(
    loadSpaceStructureSpriteImage(
      ODP_PLATFORM_CHASSIS,
      `/assets/structures/space-${odpSlug}.png`,
    ),
  );
  await Promise.all(tasks);
}

/**
 * v4M4.b — opportunistic load of all commissioned planet-structure
 * sprites. Iterates the loaded structures catalogue (`data.structures.
 * byKind` is the runtime source of truth for which kinds exist) and
 * probes `/assets/structures/planet-<kind>.png` per kind. `StructureKind`
 * enum values are already kebab-case in the type union, so the kind
 * value is used verbatim as the slug (no transformation). Documented
 * in GRAPHICS.md §4.2.
 */
async function tryLoadCommissionedPlanetStructureSprites(
  data: LoadedGameData,
): Promise<void> {
  const tasks: Promise<void>[] = [];
  for (const kind of Object.keys(data.structures.byKind)) {
    const url = `/assets/structures/planet-${kind}.png`;
    tasks.push(loadPlanetStructureSpriteImage(kind as StructureKind, url));
  }
  await Promise.all(tasks);
}

/**
 * v4M13.1 — opportunistic load of all commissioned terrain sprites.
 * Iterates the static `EnvironmentKind` list (the six terrain types
 * from `state/types.ts`) and probes `/assets/terrain/<kind>.png` per
 * kind. Race-neutral (terrain features have no per-empire tint).
 * `EnvironmentKind` values are already kebab-case in the type union,
 * so the kind value is used verbatim as the slug. Documented in
 * `TERRAIN_FEATURES.md §2.1`.
 *
 * All 6 loads fire in parallel; the returned promise resolves once
 * the slowest settles. Caller does not need to await — successes
 * mutate the cache in `terrain-sprites.ts` directly, and subsequent
 * `drawEnvironmentEntities` calls pick up the new entries via the
 * `getTerrainSprite(...)` gate.
 *
 * Pre-v4M13.2 the load attempts all 404 (no assets shipped yet) and
 * the procedural per-kind fallback in `system-view.ts` stays active
 * — visual read identical to pre-v4M13.
 */
async function tryLoadCommissionedTerrainSprites(): Promise<void> {
  // v4M13.4a — the `EnvironmentKind` rename `'gas-giant'` →
  // `'exotic-gas-cloud'` shipped; the loader probe list + asset
  // filenames are post-rename. Pre-v32 saves continue to load via
  // `deserialize.ts` lenient back-compat that translates the old
  // `'gas-giant'` value to `'exotic-gas-cloud'` on read.
  const kinds: EnvironmentKind[] = [
    'asteroid-field',
    'exotic-gas-cloud',
    'nebula',
    'black-hole',
    'ion-cloud',
    'radiation-belt',
  ];
  const tasks: Promise<void>[] = [];
  for (const kind of kinds) {
    const url = `/assets/terrain/${kind}.png`;
    tasks.push(loadTerrainSpriteImage(kind, url));
  }
  await Promise.all(tasks);
}

function enterMainMenu(ctx: AppContext): void {
  teardownCurrent(ctx);
  // v4M1.7 — start the menu music bed. Idempotent on re-entry from a
  // returned game session. Inherits the music-bus volume slider so a
  // player who muted music already hears nothing.
  getAudioEngine().startMusicBed('menu');
  const handle = mountMainMenu(document.body, {
    onNewGame: () => enterNewGameSetup(ctx),
    onLoadGame: () => openLoadPicker(ctx),
    onSettings: () => openSettings(ctx),
    onExit: () => {
      if (ctx.settings.gameplay.confirmExit && !confirm('Exit Strategos Universalis?')) {
        return;
      }
      teardownCurrent(ctx);
      // Browser can't close the tab programmatically; show a neutral
      // goodbye and leave the blank canvas. Tauri builds would call
      // window.close() here.
      const body = document.body;
      body.innerHTML =
        '<div style="position:fixed;inset:0;display:flex;align-items:center;justify-content:center;background:#05060A;color:#D7DEEA;font-family:Inter,sans-serif;font-size:16px;letter-spacing:0.06em;">Thanks for playing.</div>';
    },
  });
  ctx.mode = { kind: 'main-menu', handle };
}

function enterNewGameSetup(ctx: AppContext): void {
  teardownCurrent(ctx);
  const handle = mountNewGameSetup(document.body, ctx.data, {
    onStart: (options: NewGameOptions) => {
      const state = createInitialGameState(ctx.data, options);
      enterInGame(ctx, state);
    },
    onBack: () => enterMainMenu(ctx),
  });
  ctx.mode = { kind: 'new-game-setup', handle };
}

function enterInGame(ctx: AppContext, state: GameState): void {
  teardownCurrent(ctx);
  // v4M1.7 — stop the menu music bed; session start spins up the
  // ambient bed in its place. 0.6 s fade leaves a brief acoustic gap
  // before the in-play ambient bed's 3 s fade-in lands underneath.
  getAudioEngine().stopMusicBed('menu', 0.6);
  const session = startGameSession({
    canvas: ctx.canvas,
    data: ctx.data,
    state,
    onReturnToMainMenu: () => enterMainMenu(ctx),
    onExit: () => {
      // Session has already applied the v2M4 confirmExit gate before
      // calling here, so this path just performs the exit.
      teardownCurrent(ctx);
      document.body.innerHTML =
        '<div style="position:fixed;inset:0;display:flex;align-items:center;justify-content:center;background:#05060A;color:#D7DEEA;font-family:Inter,sans-serif;font-size:16px;letter-spacing:0.06em;">Thanks for playing.</div>';
    },
    onSave: (getState) => openSavePicker(ctx, getState),
    onAutosave: (getState) => writeAutosaveSlot(ctx, getState),
    onSaveAndReturn: (getState) => openSavePickerThenReturn(ctx, getState),
    onSettings: () => openSettings(ctx),
    getSettings: () => ctx.settings,
  });
  ctx.mode = { kind: 'in-game', session };
}

/**
 * Mount the save slot picker on top of the in-game menu. Sim is already
 * paused (the menu paused it); picker closes back to the menu on save
 * or cancel — sim resume is the player's call via Resume.
 */
function openSavePicker(ctx: AppContext, getState: () => GameState): void {
  if (ctx.savePicker !== null) return; // already open
  const close = (): void => {
    ctx.savePicker?.destroy();
    ctx.savePicker = null;
  };
  ctx.savePicker = mountSaveSlotPicker(document.body, {
    storage: ctx.storage,
    getState,
    onSaved: () => {
      close();
    },
    onCancel: close,
  });
}

/**
 * v4M1.4 — write the current game state to the dedicated `autosave`
 * slot. Called by the session's tick-driven autosave loop. The label is
 * synthesised so the load picker shows a date stamp identical to a
 * manual save without a label. Errors propagate out — the session
 * catches and logs a single warning to avoid render-loop spam.
 */
function writeAutosaveSlot(
  ctx: AppContext,
  getState: () => GameState,
): void {
  const blob = serializeGameState(getState(), { label: 'Autosave' });
  ctx.storage.write('autosave', blob);
}

/**
 * v4M1.4 — Save & Return flow. Mounts the same save picker as
 * `openSavePicker`, but on a successful save tears down the session
 * and transitions to the main menu instead of just closing the
 * picker. Cancel from the picker behaves like a normal cancel —
 * the player stays in-game. No confirm gate on the return: the
 * explicit Save & Return click is the consent.
 */
function openSavePickerThenReturn(
  ctx: AppContext,
  getState: () => GameState,
): void {
  if (ctx.savePicker !== null) return; // already open
  const close = (): void => {
    ctx.savePicker?.destroy();
    ctx.savePicker = null;
  };
  ctx.savePicker = mountSaveSlotPicker(document.body, {
    storage: ctx.storage,
    getState,
    onSaved: () => {
      close();
      enterMainMenu(ctx);
    },
    onCancel: close,
  });
}

/**
 * Mount the Settings overlay (v2M4). Reachable from both the main menu
 * and the in-game menu — the overlay is z-1100 and sits cleanly on top
 * of either. On Save: persist via SettingsStorage and update ctx.settings
 * so the next caller of `getSettings` sees the new values immediately.
 * On Cancel: close, no persistence.
 */
function openSettings(ctx: AppContext): void {
  if (ctx.settingsOverlay !== null) return;
  const close = (): void => {
    ctx.settingsOverlay?.destroy();
    ctx.settingsOverlay = null;
  };
  ctx.settingsOverlay = mountSettingsOverlay(document.body, {
    current: ctx.settings,
    onSave: (next) => {
      ctx.settings = next;
      ctx.settingsStorage.save(next);
      // v2M8.8 — push the latest audio volumes into the engine so the
      // change takes effect immediately without waiting for next session.
      getAudioEngine().applyVolumes(next.audio);
      close();
    },
    onCancel: close,
  });
}

/**
 * Mount the load slot picker on top of the main menu. On Load → restore
 * the deserialised state into a new in-game session, replacing the main
 * menu. On Cancel → close the picker, leave the main menu visible.
 */
function openLoadPicker(ctx: AppContext): void {
  if (ctx.loadPicker !== null) return;
  const close = (): void => {
    ctx.loadPicker?.destroy();
    ctx.loadPicker = null;
  };
  ctx.loadPicker = mountLoadSlotPicker(document.body, {
    storage: ctx.storage,
    data: ctx.data,
    onLoaded: (state) => {
      close();
      enterInGame(ctx, state);
    },
    onCancel: close,
  });
}

function teardownCurrent(ctx: AppContext): void {
  // Close any picker / overlay that's mounted before tearing down the
  // mode beneath it — keeps overlay z-order tidy on transitions.
  if (ctx.savePicker !== null) {
    ctx.savePicker.destroy();
    ctx.savePicker = null;
  }
  if (ctx.loadPicker !== null) {
    ctx.loadPicker.destroy();
    ctx.loadPicker = null;
  }
  if (ctx.settingsOverlay !== null) {
    ctx.settingsOverlay.destroy();
    ctx.settingsOverlay = null;
  }
  if (!ctx.mode) return;
  switch (ctx.mode.kind) {
    case 'main-menu':
      ctx.mode.handle.destroy();
      break;
    case 'new-game-setup':
      ctx.mode.handle.destroy();
      break;
    case 'in-game':
      ctx.mode.session.destroy();
      break;
  }
  ctx.mode = null;
}

function showBootstrapError(err: unknown): void {
  const el = document.getElementById('bootstrap-error');
  if (!el) return;
  const msg = err instanceof Error ? `${err.message}\n${err.stack ?? ''}` : String(err);
  el.textContent = `Bootstrap error: ${msg}`;
  el.style.display = 'flex';
}

bootstrap();
